import { MeasureData } from "./interfaces";
import { allMeasurementsFitnessMale, allMeasurementsFitnessFemale, allMeasurementsRetailMale, allMeasurementsRetailFemale } from "./constants";

// src/config/domainConfig.ts
export interface KeyLabel {
    key: keyof MeasureData["measurements"],
    label: string,
    unit: string
};
export interface Config {
    isDev: boolean,
    use4views: boolean,
    // Muscle mass is an estimate, so it ships to dev.sizewall.com only until the
    // Sizewall team has verified it.
    showMuscleMass: boolean,
    /**
     * Whether a scan cannot start without today's weight. Body composition
     * splits fat from lean in kilograms and needs one; a fitting does not.
     */
    requiresWeight: boolean,
    strings: {
        /** The document title. */
        title: string;
        /** What the person being measured is called: "Client", "Customer". */
        profile: string;
    },
    measurements: {
        male: KeyLabel[];
        female: KeyLabel[];
    },
    /**
     * The fitness surfaces, each on or off. Retail measures for fit and has
     * none of them; read through hasFeature (lib/features.ts), not here.
     */
    features: Features,
}

export interface Features {
    /** Fat, muscle and other lean in kilograms: the composition card and chart. */
    composition: boolean;
    /** A body fat target, its projection and the progress toward it. */
    goal: boolean;
    /** Weight in kilograms shown beside a scan. */
    weight: boolean;
    /** Body fat percent and its bands (Underfat … Obese). */
    bodyFat: boolean;
    /**
     * A download of the avatar as an OBJ for garment design tools (CLO3D).
     * Retail's: the people who fit clothes are the ones who draft patterns.
     */
    avatarExport: boolean;
}

export type Feature = keyof Features;

const FITNESS: Features = { composition: true, goal: true, weight: true, bodyFat: true, avatarExport: false };
const RETAIL: Features = { composition: false, goal: false, weight: false, bodyFat: false, avatarExport: true };

const configs: Record<string, Config> = {
    "app": {
        requiresWeight: true,
        isDev: false,
        use4views: false,
        showMuscleMass: false,
        strings: {
            title: "Sizewall",
            profile: "Client"
        },
        measurements: {
            male: allMeasurementsFitnessMale,
            female: allMeasurementsFitnessFemale
        },
        features: FITNESS,
    },
    "web": {
        requiresWeight: true,
        isDev: false,
        use4views: false,
        showMuscleMass: false,
        strings: {
            title: "Sizewall",
            profile: "Client"
        },
        measurements: {
            male: allMeasurementsFitnessMale,
            female: allMeasurementsFitnessFemale
        },
        features: FITNESS,
    },
    "health": {
        requiresWeight: true,
        isDev: false,
        use4views: false,
        showMuscleMass: false,
        strings: {
            title: "Sizewall",
            profile: "Client"
        },
        measurements: {
            male: allMeasurementsFitnessMale,
            female: allMeasurementsFitnessFemale
        },
        features: FITNESS,
    },
    "dev": {
        requiresWeight: true,
        isDev: true,
        use4views: true,
        showMuscleMass: true,
        strings: {
            title: "Sizewall",
            profile: "Client"
        },
        measurements: {
            male: allMeasurementsFitnessMale,
            female: allMeasurementsFitnessFemale
        },
        features: FITNESS,
    },
    "retail": {
        requiresWeight: false,
        isDev: false,
        use4views: false,
        showMuscleMass: false,
        strings: {
            title: "Sizewall Retail",
            profile: "Customer"
        },
        measurements: {
            male: allMeasurementsRetailMale,
            female: allMeasurementsRetailFemale
        },
        features: RETAIL,
    },

};

export function isLocalDomain(domain: string): boolean {
    return (
        domain === "localhost" ||
        domain.endsWith(".localhost") ||
        domain === "::1" ||
        domain === "[::1]" ||
        domain === "0.0.0.0" ||
        domain.startsWith("127.") ||
        domain.endsWith(".local") ||
        /^10(?:\.\d{1,3}){3}$/.test(domain) ||
        /^172\.(1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2}$/.test(domain) ||
        /^192\.168(?:\.\d{1,3}){2}$/.test(domain) ||
        /^169\.254(?:\.\d{1,3}){2}$/.test(domain)
    );
}

/** The config a hostname gets on its own, before any override. */
export function domainVersion(hostname: string): string {
    const domain = hostname.toLowerCase();
    if (isLocalDomain(domain) || domain === "dev.sizewall.com") return "dev";
    if (domain === "web.sizewall.com") return "web";
    if (domain.includes("retail")) return "retail";
    return "app";
}

/** Where a `?experience=` choice is kept, so it survives a reload or a new link. */
export const EXPERIENCE_KEY = "sizewall.experience";

type ExperienceStore = Pick<Storage, "getItem" | "setItem">;

/**
 * The config key for this page: the hostname's, or on a local or dev host a
 * `?experience=retail` (or `app`, or any config key) chosen for a demo.
 *
 * The override is honoured only where the hostname would say "dev". On a
 * customer's host a query string must not turn one product into another.
 * The choice is kept in sessionStorage so in-app links, which drop the query,
 * keep it; storage can throw (private mode, blocked site data), and the
 * override then lasts as long as the query does.
 */
export function resolveExperience(
    hostname: string,
    search: string,
    storage?: ExperienceStore,
): string {
    const fromHost = domainVersion(hostname);
    if (fromHost !== "dev") return fromHost;

    const isKey = (k: string | null): k is string => !!k && Object.hasOwn(configs, k);
    const asked = new URLSearchParams(search).get("experience");
    if (isKey(asked)) {
        try {
            storage?.setItem(EXPERIENCE_KEY, asked);
        } catch {
            // Not kept; the query still applies to this load.
        }
        return asked;
    }
    try {
        const kept = storage?.getItem(EXPERIENCE_KEY) ?? null;
        if (isKey(kept)) return kept;
    } catch {
        // Unreadable storage is no override.
    }
    return fromHost;
}

function getCurrentDomainVersion(): string {
    if (typeof window === "undefined") return "app"; // SSR or testing
    let storage: ExperienceStore | undefined;
    try {
        storage = window.sessionStorage;
    } catch {
        storage = undefined;
    }
    return resolveExperience(window.location.hostname, window.location.search, storage);
}

export const currentConfig = (() => {
    const domain = getCurrentDomainVersion();
    console.log("Current domain version:", domain);
    return configs[domain] || configs["app"];
})();

export function getConfigForExperience(
    experience?: { measurementDisplay?: string }
): Config {
    const key = experience?.measurementDisplay;
    if (key && Object.hasOwn(configs, key)) {
        return configs[key];
    }
    return currentConfig;
}
